import { useCallback, useEffect, useRef, useState } from "react";
import Router from "next/router";

import { Card, Alert, Spin, Button } from "antd";

import SiteLayout from "~/components/layout/SiteLayout";
import { verifyCaptcha, clearVerificationCookie } from "~/utils/verification";
import settings from "~/settings";

const VerificationPage = (props) => {
  const { redirect } = props;

  const [verificationState, setVerificationState] = useState({
    verifying: false,
    error: null,
  });
  const [widgetLoaded, setWidgetLoaded] = useState(false);
  const isVerifyingRef = useRef(false);
  const hasInitializedRef = useRef(false);
  const initStartedAtRef = useRef(0);

  const onBizResultCallback = useCallback(
    (bizResult) => {
      if (bizResult === true) {
        Router.replace(redirect || "/");
        return;
      }

      isVerifyingRef.current = false;
      setVerificationState({
        verifying: false,
        error: "人机验证失败，请重试",
      });
    },
    [redirect]
  );

  const captchaVerifyCallback = useCallback(
    async (captchaVerifyParam, callback) => {
      const formatResult = (captchaResult, bizResult) => ({
        captchaResult,
        bizResult,
      });

      const complete = (result) => {
        if (typeof callback === "function") {
          callback(result);
          return;
        }
        return result;
      };

      if (isVerifyingRef.current) {
        return complete(formatResult(false, false));
      }

      // 官方要求验证码初始化与业务验证请求间隔至少 2 秒。
      if (Date.now() - initStartedAtRef.current < 2000) {
        setVerificationState({
          verifying: false,
          error: "验证码加载中，请等待 2 秒后重试",
        });
        return complete(formatResult(false, false));
      }

      isVerifyingRef.current = true;
      setVerificationState({
        verifying: true,
        error: null,
      });

      try {
        await verifyCaptcha({ captchaVerifyParam });
        return complete(formatResult(true, true));
      } catch (err) {
        isVerifyingRef.current = false;
        setVerificationState({
          verifying: false,
          error: err.message || "验证失败，请重试",
        });
        return complete(formatResult(false, false));
      }
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    const sceneId = settings.aliyunCaptchaSceneId;

    if (!sceneId || sceneId === "your_scene_id_here") {
      setVerificationState({
        verifying: false,
        error: "验证码配置缺失，请联系管理员配置阿里云场景 ID",
      });
      return;
    }

    const tryInitCaptcha = () => {
      if (cancelled || hasInitializedRef.current) {
        return;
      }

      if (typeof window === "undefined") {
        return;
      }

      if (typeof window.initAliyunCaptcha !== "function") {
        return;
      }

      hasInitializedRef.current = true;
      initStartedAtRef.current = Date.now();

      window.initAliyunCaptcha({
        SceneId: sceneId,
        mode: "popup",
        element: "#captcha-element",
        button: "#captcha-button",
        captchaVerifyCallback,
        onBizResultCallback,
        getInstance: (instance) => {
          // 等待 2 秒后显示验证按钮
          setTimeout(() => {
            setWidgetLoaded(true);
          }, 2000);
        },
        slideStyle: {
          width: 360,
          height: 40,
        },
        language: "cn",
        immediate: true,
      });
    };

    tryInitCaptcha();

    const initPollingTimer = window.setInterval(() => {
      if (hasInitializedRef.current) {
        window.clearInterval(initPollingTimer);
        return;
      }
      tryInitCaptcha();
    }, 200);

    const initTimeoutTimer = window.setTimeout(() => {
      if (!hasInitializedRef.current && !cancelled) {
        setVerificationState({
          verifying: false,
          error: "验证组件加载失败，请刷新页面重试",
        });
      }
    }, 10000);

    return () => {
      cancelled = true;
      window.clearInterval(initPollingTimer);
      window.clearTimeout(initTimeoutTimer);
    };
  }, [captchaVerifyCallback, onBizResultCallback]);

  const handleRetry = () => {
    isVerifyingRef.current = false;
    clearVerificationCookie();
    window.location.reload();
  };

  return (
    <SiteLayout>
      <Card
        style={{ textAlign: "center", padding: "60px 20px", height: "100%" }}
      >
        {widgetLoaded && (
          <p
            style={{
              fontSize: "14px",
              marginTop: 10,
              marginBottom: 30,
              color: "#a8a8a8",
            }}
          >
            请点击验证按钮完成验证
          </p>
        )}
        {!verificationState.error && !widgetLoaded && (
          <div style={{ marginTop: 20 }}>
            <Spin tip="验证码加载中..." />
          </div>
        )}
        {!verificationState.error && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              marginTop: 20,
            }}
          >
            <div id="captcha-element" />
            <Button
              id="captcha-button"
              type="primary"
              block
              size="middle"
              style={{
                display: widgetLoaded ? "block" : "none",
                maxWidth: 200,
                width: "50%",
              }}
            >
              点击验证
            </Button>
          </div>
        )}
        {verificationState.verifying && (
          <div style={{ marginTop: 20 }}>
            <Spin tip="验证中..." />
          </div>
        )}
        {verificationState.error && (
          <div style={{ marginTop: 16 }}>
            <Alert
              type="error"
              message={verificationState.error}
              style={{ marginBottom: 16 }}
            />
            <Button
              type="primary"
              onClick={handleRetry}
            >
              重新验证
            </Button>
          </div>
        )}
      </Card>
    </SiteLayout>
  );
};

VerificationPage.requireUser = () => false;

VerificationPage.getInitialProps = async (ctx) => {
  const { query } = ctx;
  const { redirect } = query;

  return {
    redirect: redirect ? decodeURIComponent(redirect) : null,
  };
};

export default VerificationPage;
